import { create } from 'zustand';
import type { CallOutcome, CallType } from '@/api/types';
import {
  CallProviderError,
  getCallProvider,
  type CallErrorKind,
  type CallSession,
} from '@/calls/CallProvider';
import { getSocket } from '@/realtime/sockets';
import { tabs } from '@/realtime/tabChannel';
import { useSessionStore } from './sessionStore';

import { tNow } from '@/i18n/languageStore';

/**
 * The current call (data-model.md B1), shared by the call overlay, the
 * ringer and the call buttons. Only the tab that started or answered a call
 * joins its media; the others just ring and stop.
 *
 * idle → outgoing-ringing | incoming-ringing → connecting → active → ended → idle
 */
export type CallPhase =
  'idle' | 'outgoing-ringing' | 'incoming-ringing' | 'connecting' | 'active' | 'ended';

export interface CallCounterpart {
  id: string;
  name: string;
  photoUrl?: string | null;
}

interface CallState {
  phase: CallPhase;
  callId: string | null;
  direction: 'outgoing' | 'incoming' | null;
  callType: CallType;
  counterpart: CallCounterpart | null;
  muted: boolean;
  cameraOn: boolean;
  /** The device has another camera to switch to (front ↔ rear). */
  canSwitchCamera: boolean;
  /** When it started ringing (ms), for the "Calling… 00:08" timer. */
  ringingSince: number | null;
  /** When the media connected (ms), for the call-duration timer. */
  activeSince: number | null;
  outcome: CallOutcome | null;
  error: CallErrorKind | null;
  /** When the server will hang up (ms): a call lasts 30 minutes and can be extended. */
  endsAt: number | null;
  /** The end is near: the call shows how long is left, and offers to extend it. */
  endingSoon: boolean;
  /** Whether the call can still be extended. */
  canExtend: boolean;
  /** Why it ended, when it was not someone hanging up. */
  endedReason: 'time-limit' | null;
  /** This tab started or answered the call, so it holds the media. */
  ownedByThisTab: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  reset: () => void;
}

const initial = {
  phase: 'idle' as CallPhase,
  callId: null,
  direction: null,
  callType: 'audio' as CallType,
  counterpart: null,
  muted: false,
  cameraOn: true,
  canSwitchCamera: false,
  ringingSince: null,
  activeSince: null,
  outcome: null,
  error: null,
  endsAt: null,
  endingSoon: false,
  canExtend: true,
  endedReason: null,
  ownedByThisTab: false,
  localStream: null,
  remoteStream: null,
};

export const useCallStore = create<CallState>()((set) => ({
  ...initial,
  reset: () => set({ ...initial }),
}));

/** How long the "call ended" view stays before the overlay closes. */
export const ENDED_DISPLAY_MS = 3_000;
const SIGNAL_TIMEOUT_MS = 10_000;

let session: CallSession | null = null;
let stopRemote: (() => void) | null = null;
let endedTimer: ReturnType<typeof setTimeout> | undefined;

const get = () => useCallStore.getState();
const set = (patch: Partial<CallState>) => useCallStore.setState(patch);
const isCurrent = (callId: string) => get().callId === callId && get().phase !== 'idle';

function emit(event: string, payload: unknown) {
  getSocket('calls')?.emit(event, payload);
}

async function emitWithAck<T>(event: string, payload: unknown): Promise<T> {
  const socket = getSocket('calls');
  if (!socket) throw new Error('Not connected');
  return (await socket.timeout(SIGNAL_TIMEOUT_MS).emitWithAck(event, payload)) as T;
}

async function leaveMedia() {
  stopRemote?.();
  stopRemote = null;
  const current = session;
  session = null;
  await current?.leave().catch(() => undefined);
}

function finish(patch: {
  outcome?: CallOutcome | null;
  error?: CallErrorKind | null;
  reason?: 'time-limit' | null;
}) {
  const callId = get().callId;
  void leaveMedia();
  set({
    phase: 'ended',
    outcome: patch.outcome ?? null,
    error: patch.error ?? null,
    endedReason: patch.reason ?? null,
    endingSoon: false,
    localStream: null,
    remoteStream: null,
  });
  clearTimeout(endedTimer);
  endedTimer = setTimeout(() => {
    if (get().phase === 'ended' && get().callId === callId) get().reset();
  }, ENDED_DISPLAY_MS);
}

function errorKind(error: unknown): CallErrorKind {
  return error instanceof CallProviderError ? error.kind : 'failed';
}

// ---------- Actions from this tab ----------

export async function startCall(counterpart: CallCounterpart, callType: CallType): Promise<void> {
  if (get().phase !== 'idle') return;
  clearTimeout(endedTimer);
  set({
    ...initial,
    phase: 'outgoing-ringing',
    direction: 'outgoing',
    ringingSince: Date.now(),
    callType,
    counterpart,
    ownedByThisTab: true,
  });
  try {
    await (await getCallProvider()).preflight(callType);
  } catch (error) {
    finish({ error: errorKind(error) });
    return;
  }
  try {
    const call = await emitWithAck<{ id?: string; error?: string }>('call:initiate', {
      calleeId: counterpart.id,
      callType,
    });
    if (call.error) {
      // Refused at once (blocked, or nobody to call): say so, instead of staying on "Calling…".
      if (get().phase === 'outgoing-ringing') {
        finish({
          error: call.error === 'blocked' || call.error === 'unavailable' ? call.error : 'failed',
        });
      }
      return;
    }
    if (get().phase === 'outgoing-ringing') set({ callId: call.id });
    else if (call.id) emit('call:end', { callId: call.id }); // cancelled while it was being placed
  } catch {
    finish({ error: 'failed' });
  }
}

export async function acceptCall(): Promise<void> {
  const { phase, callId, callType } = get();
  if (phase !== 'incoming-ringing' || !callId) return;
  set({ ownedByThisTab: true, phase: 'connecting' });
  tabs.post({ type: 'call:taken', callId });
  try {
    await (await getCallProvider()).preflight(callType);
  } catch (error) {
    emit('call:decline', { callId });
    finish({ error: errorKind(error) });
    return;
  }
  try {
    await emitWithAck('call:accept', { callId });
  } catch {
    finish({ error: 'failed' });
  }
}

export async function declineCall(): Promise<void> {
  const { phase, callId } = get();
  if (phase !== 'incoming-ringing' || !callId) return;
  emit('call:decline', { callId });
  tabs.post({ type: 'call:taken', callId });
  get().reset();
}

export async function endCall(): Promise<void> {
  const { phase, callId } = get();
  if (phase === 'idle' || phase === 'ended') return;
  if (callId) emit('call:end', { callId });
  finish({ outcome: phase === 'outgoing-ringing' ? 'cancelled' : 'completed' });
}

/** Asks for another stretch of time on the call (either person can). */
export async function extendCall(): Promise<void> {
  const { phase, callId } = get();
  if (phase !== 'active' && phase !== 'connecting') return;
  if (!callId) return;
  try {
    const result = await emitWithAck<{ ok?: boolean; endsAt?: string; canExtend?: boolean }>(
      'call:extend',
      {
        callId,
      },
    );
    if (result.ok && result.endsAt) {
      set({
        endsAt: Date.parse(result.endsAt),
        endingSoon: false,
        canExtend: Boolean(result.canExtend),
      });
    } else {
      set({ canExtend: false });
    }
  } catch {
    // No answer: the warning stays, and the button can be pressed again.
  }
}

export function toggleMute() {
  const muted = !get().muted;
  session?.setMuted(muted);
  set({ muted });
}

export function toggleCamera() {
  const cameraOn = !get().cameraOn;
  session?.setCameraOn(cameraOn);
  set({ cameraOn });
}

/** Front ↔ rear camera during a video call; on failure the current camera stays. */
export async function switchCamera() {
  if (!session?.canSwitchCamera) return;
  try {
    const stream = await session.switchCamera();
    // A new MediaStream object, so the preview element picks up the new camera.
    if (stream) {
      set({
        localStream:
          typeof MediaStream !== 'undefined' && stream instanceof MediaStream
            ? new MediaStream(stream.getTracks())
            : stream,
      });
    }
  } catch (error) {
    console.error('[calls] switching the camera failed', error);
  }
}

/** Closes the "call ended" view now. */
export function dismissCall() {
  if (get().phase === 'ended') get().reset();
}

// ---------- Events from the server and other tabs ----------

export function handleIncoming(payload: {
  callId: string;
  callerId: string;
  callType: CallType;
  callerName?: string;
  callerPhotoUrl?: string | null;
}) {
  // Busy: this call rings on elsewhere and, unanswered, ends as missed.
  if (get().phase !== 'idle' && get().phase !== 'ended') return;
  clearTimeout(endedTimer);
  set({
    ...initial,
    phase: 'incoming-ringing',
    callId: payload.callId,
    direction: 'incoming',
    ringingSince: Date.now(),
    callType: payload.callType,
    counterpart: {
      id: payload.callerId,
      name: payload.callerName ?? tNow("Neighbor"),
      photoUrl: payload.callerPhotoUrl ?? undefined,
    },
  });
}

export function handleAccepted({ callId }: { callId: string }) {
  if (isCurrent(callId) && get().phase === 'outgoing-ringing') set({ phase: 'connecting' });
}

export function handleDeclined({ callId }: { callId: string }) {
  if (isCurrent(callId) && get().direction === 'outgoing') finish({ outcome: 'declined' });
}

/** The end of the call is near: show how long is left, and whether it can be extended. */
export function handleEndingSoon({
  callId,
  endsAt,
  canExtend,
}: {
  callId: string;
  endsAt: string;
  canExtend?: boolean;
}) {
  if (!isCurrent(callId)) return;
  set({ endsAt: Date.parse(endsAt), endingSoon: true, canExtend: canExtend ?? false });
}

/** Either person extended the call: it ends later, and the warning goes. */
export function handleExtended({
  callId,
  endsAt,
  canExtend,
}: {
  callId: string;
  endsAt: string;
  canExtend?: boolean;
}) {
  if (!isCurrent(callId)) return;
  set({ endsAt: Date.parse(endsAt), endingSoon: false, canExtend: canExtend ?? false });
}

export function handleProviderSession({
  callId,
  providerSessionId,
  endsAt,
}: {
  callId: string;
  providerSessionId: string;
  endsAt?: string;
}) {
  if (endsAt && isCurrent(callId)) set({ endsAt: Date.parse(endsAt) });
  const state = get();
  if (!isCurrent(callId) || !state.ownedByThisTab || session) return;
  if (state.phase === 'outgoing-ringing') set({ phase: 'connecting' });
  const memberId = useSessionStore.getState().member?.id ?? '';
  void (async () => {
    try {
      const provider = await getCallProvider();
      const joined = await provider.join({
        callId,
        roomId: providerSessionId,
        memberId,
        callType: state.callType,
      });
      if (!isCurrent(callId)) {
        await joined.leave();
        return;
      }
      session = joined;
      if (get().muted) joined.setMuted(true);
      set({ localStream: joined.localStream, canSwitchCamera: joined.canSwitchCamera });
      stopRemote = joined.onRemoteStream((stream) => {
        if (!isCurrent(callId)) return;
        if (stream) {
          set({
            remoteStream: stream,
            phase: 'active',
            activeSince: get().activeSince ?? Date.now(),
          });
        } else {
          set({ remoteStream: null });
        }
      });
    } catch (error) {
      if (!isCurrent(callId)) return;
      emit('call:end', { callId });
      finish({ outcome: 'failed', error: errorKind(error) });
    }
  })();
}

export function handleEnded({
  callId,
  outcome,
  reason,
}: {
  callId: string;
  outcome: CallOutcome;
  durationSeconds?: number;
  reason?: 'time-limit';
}) {
  if (!isCurrent(callId)) return;
  if (get().phase === 'ended') {
    // Already showing the end (this tab hung up): take the server's word for how it ended.
    set({ outcome, endedReason: reason ?? null });
    return;
  }
  if (get().phase === 'incoming-ringing' && !get().ownedByThisTab && outcome !== 'missed') {
    get().reset(); // the caller gave up: nothing to show
    return;
  }
  finish({ outcome, reason });
}

/** Another device of mine answered (server event) or another tab here did (tab channel). */
export function handleAnsweredElsewhere({ callId }: { callId: string }) {
  if (isCurrent(callId) && get().phase === 'incoming-ringing' && !get().ownedByThisTab) {
    get().reset();
  }
}

export function handleCallTaken(callId: string) {
  handleAnsweredElsewhere({ callId });
}

/** Sign-out: hang up and forget the call. */
export function resetCall() {
  const { callId, phase } = get();
  if (callId && phase !== 'idle' && phase !== 'ended') emit('call:end', { callId });
  clearTimeout(endedTimer);
  void leaveMedia();
  get().reset();
}
